import React, { useEffect, useState } from 'react';
import { Form, Spin } from 'components/ds';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../PageContainer/PageContainer';
import { FormWrapper } from '../../../../components/Css';
import ProgressStepper from '../../../../components/ProgressStepper';
import { useGetTemplate } from 'modules/account/configs/SchemaConfig';
import { ApiLocalStorage } from 'utils/ApiLocalStorage';
import { FormFields, FormFooter, ProgressStepperWrapper, StyledForm } from './DynamicForm.styles';
import { DynamicFormProps, FormItemProps } from '../../_types/dynamicFormInterfaces.types';
import { renderSelectItem } from '../renderSelectItem/renderSelectItem';
import { renderInputItem } from '../renderInputItem/renderInputItem';
import { shouldDisableField } from '../../utils/shouldDisableField';
import { useExistingIdentifiers } from '../../context/ExistingIdentifiersContext';
import { useDynamicFieldsStorage } from '../../hooks/useDynamicFieldsStorage';

export interface ExistingIdentifiers {
  email: boolean;
  phoneNumber: boolean;
}

export const DynamicForm: React.FC<DynamicFormProps> = ({
                                                          pageName,
                                                          onSubmit,
                                                          initialValues,
                                                          form: externalForm,
                                                          children,
                                                          loadingFields = {}
                                                        }) => {
  const [internalForm] = Form.useForm();
  const form = externalForm || internalForm;
  const navigate = useNavigate();
  const {isLoading, data: pageConfig = {}} = useGetTemplate(pageName);
  const [isFormValid, setIsFormValid] = useState(false);
  const {updateStorageData, registrationData} = useDynamicFieldsStorage();
  
  const formValidator = Form.useWatch([], form);
  const {existingIdentifiers, isVerifying} = useExistingIdentifiers();
  const hasExistingIdentifiers = existingIdentifiers.email || existingIdentifiers.phoneNumber;
  
  // Mapeamento de pageName para chave do storage
  const getStorageKey = (pageName: string): keyof typeof registrationData | null => {
    const keyMapping: { [key: string]: keyof typeof registrationData } = {
      'personal': 'personalInfo',
      'contact': 'contactInfo',
      'address': 'addressInfo',
      'additionalInfo': 'additionalInfo',
      'complementaryStep': 'complementaryInfo'
    };
    
    return keyMapping[pageName] || null;
  };
  
  useEffect(() => {
    form.validateFields({validateOnly: true}).then(
      () => {
        setIsFormValid(true);
      },
      () => {
        setIsFormValid(false);
      }
    );
  }, [formValidator, form]);
  
  useEffect(() => {
    // Carrega dados salvos do storage para o formulário
    const storageKey = getStorageKey(pageName);
    const savedData = storageKey ? registrationData[storageKey] : null;
    
    if (savedData) {
      form.setFieldsValue(savedData);
    } else if (initialValues && form) {
      form.setFieldsValue(initialValues);
    }
  }, [form, initialValues, pageName, registrationData]);
  
  const processInitialValues = (item: FormItemProps): FormItemProps => {
    const storageKey = getStorageKey(pageName);
    const savedData = storageKey ? registrationData[storageKey] : null;
    
    // Prioriza dados salvos, depois initialValues
    const valueToUse = savedData?.[item.name] || initialValues?.[item.name];
    
    if (valueToUse) {
      const fieldValue = typeof valueToUse === 'object' ? valueToUse.value : valueToUse;
      const fieldDisabled = typeof valueToUse === 'object' ? valueToUse.disabled : false;
      
      return {
        ...item,
        disabled: fieldDisabled || item.disabled,
        initialValue: fieldValue || item.value
      };
    }
    
    return item;
  };
  
  const renderFormItem = (item: FormItemProps) => {
    const processedItem = processInitialValues(item);
    
    const userData = initialValues || {};
    
    const isDisabled = shouldDisableField(
      item.name,
      userData,
      processedItem.disabled
    );
    
    // Mapeia campos do tipo Enum para Select
    const fieldType = processedItem.type === 'Enum' ? 'select' : processedItem.type;
    
    switch (fieldType) {
      case 'select':
        return renderSelectItem(processedItem, isDisabled);
      default:
        return renderInputItem(processedItem, isDisabled, loadingFields[processedItem.name], form);
    }
  };
  
  const onHandleSubmit = async (values: any) => {
    // Salva no local storage tradicional (compatibilidade)
    ApiLocalStorage.set(pageName, values);
    
    // Salva no storage dinâmico usando a chave correta
    const storageKey = getStorageKey(pageName);

    if (storageKey) {
      updateStorageData(storageKey, values);
    }
    
    if (onSubmit) {
      await onSubmit();
    }
    
    navigate(pageConfig.navigateTo());
  };
  
  if (isLoading) {
    return (
      <PageContainer title="">
        <FormWrapper>
          <Spin style={{marginBottom: '65px'}} size="large"/>
        </FormWrapper>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer
      title={pageConfig.title || 'Título'}
      navigateBack={pageConfig?.navigateBack?.()}
      navigateClose
      thereIsButton
      buttonText={pageConfig.buttonLabel || 'Próximo'}
      onClick={() => form?.submit()}
      loading={isLoading}
      disabled={!isFormValid || hasExistingIdentifiers || isVerifying}
    >
      <ProgressStepperWrapper>
        <ProgressStepper
          totalSteps={pageConfig.totalSteps}
          currentStep={pageConfig.currentStep}
        />
      </ProgressStepperWrapper>
      
      <FormWrapper>
        <StyledForm
          form={form}
          name="form"
          layout="vertical"
          onFinish={onHandleSubmit}
        >
          <FormFields>
            {(pageConfig.formFields || []).map((item: FormItemProps) =>
              renderFormItem(item)
            )}
          </FormFields>
          
          <FormFooter>
            {children}
          </FormFooter>
        </StyledForm>
      </FormWrapper>
    </PageContainer>
  );
};
